import { closeCircleOutline } from 'ionicons/icons';

import { ToastButton, ToastOptions } from '@ionic/react';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem, ReadFileResult } from '@capacitor/filesystem';
import { format } from 'date-fns/format';
import { FirebaseApp, getApp } from 'firebase/app';
import {
  Auth,
  getAuth as _getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth';
import { Timestamp } from 'firebase/firestore/lite';
import _ from 'lodash';
import { getSnapshot } from 'mobx-state-tree';
import { BluetoothInfo } from 'models/BluetoothInfo';
import { UserSnapshot } from 'models/User';
import { ICloudStorage } from 'services/Database/CloudStorageService';
import {
  RunModel,
  RunModelIn,
  RunModelModel,
  RunModelOut,
} from '../models/RunModel/RunModel';
import {
  BLEDongleState,
  BLEStatus,
  BluetoothEntry,
  Bufferable,
  ByteCountable,
  ClassConstructor,
  ST,
  Stance,
  _BLEStatus,
} from '../types';
import { Device } from './device';
import { DongleState } from 'types/communication';
import { Vector3 } from 'three';
import firebaseConfig from '../config/firebase.json';
import { initializeApp, getApps } from 'firebase/app';
import { RenderData, TimedData } from 'types/storage';

export const isInDebugMode = () => {
  return process.env.NODE_ENV === 'development';
};

let _auth: Auth | null = null;
export const getAuth = (app?: FirebaseApp): Auth => {
  if (!_auth) {
    let _app: FirebaseApp;
    if (app) {
      _app = app;
    } else {
      if (getApps().length === 0) {
        _app = initializeApp(firebaseConfig);
      } else {
        _app = getApp();
      }
    }
    if (Capacitor.isNativePlatform()) {
      _auth = initializeAuth(_app, { persistence: indexedDBLocalPersistence });
    } else {
      _auth = _getAuth(_app);
    }
  }
  return _auth!;
};

export const makeErrorToast = (
  header: string,
  message: string,
  close: () => void,
  duration: number = 2000
) => {
  const toastCloseBtn: ToastButton = {
    icon: closeCircleOutline,
    handler: close,
  };
  const toastOptions: ToastOptions = {
    header,
    message,
    buttons: [toastCloseBtn],
    duration,
  };
  return toastOptions;
};

export const base64ToBuffer = (base64: string) => {
  var binary_string = window.atob(base64);
  return Uint8Array.from(binary_string, (c) => c.charCodeAt(0)).buffer;
};
export const bufferToBase64 = (ab: ArrayBuffer) => {
  return blobToBase64(aToBlob(ab));
};

export const aToBlob = (ab: ArrayBuffer) => new Blob([ab]);
export const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader();
  return new Promise<string>((resolve, reject) => {
    reader.onerror = reject;
    reader.onload = () => {
      resolve((reader.result as string).split('base64,')[1]);
    };
    reader.readAsDataURL(blob);
  });
};

export const listToArrayBuffer = <T extends Bufferable>(
  samples: Array<T | object>,
  cls: ByteCountable<T>
): ArrayBuffer => {
  if (samples.length === 0) return new ArrayBuffer(0);

  // let tmp = new Uint8Array(cls.byteCount() * samples.length);
  let buf = new ArrayBuffer(cls.byteCount() * samples.length);
  let offset = 0;
  for (const sample of samples) {
    const _sample = new cls();
    _.assign(_sample, sample);
    // const buf = tmp.slice(offset).buffer;
    _sample.toArrayBuffer(buf, offset);
    offset += cls.byteCount();
  }
  return buf;
};

export const arrayBufferToList = <T extends Bufferable>(
  buffer: ArrayBuffer,
  cls: ByteCountable<T>
): Array<T> => {
  let ret: Array<T> = [];
  for (let i = 0; i < buffer.byteLength; i += cls.byteCount()) {
    ret.push(cls.ofArrayBuffer(buffer, i));
  }
  return ret;
};

export const trickFromStore = async (
  trick: RunModelIn,
  _storage: ICloudStorage
): Promise<RunModel> => {
  let date: Date | undefined = undefined;
  if (typeof trick.date === 'number') date = new Date(trick.date);
  else if (trick.date instanceof Timestamp) date = trick.date.toDate();

  const localVideoFileName = getVideoFileName(trick.deviceId, trick.recordId);
  const out = RunModelModel.create(
    _.defaults({ date, localVideoFileName }, trick)
  );

  // TODO batch this if possible!
  // we can't call `out.downloadVideo()` as the RunModelModel doesn't have an environment yet
  return out;
};

export const trickToStore = (
  trick: RunModel
): RunModelOut & Record<string, any> => {
  return _.omit(
    _.assign({}, getSnapshot(trick), {
      date: trick.date, // keep the date as Date object
    }),
    [
      'playData',
      'recordData',
      'favorite',
      'localVideoFileName',
      'localVideoUri',
    ]
  );
};

/** Convert a milliseconds to the corresponding SS:MS timestamp
 */
export const msToTime = (frames: number, part?: 'seconds' | 'millis') => {
  const d = new Date(frames);
  const seconds = d.getUTCMinutes() * 60 + d.getUTCSeconds();
  const millis = Math.floor(d.getUTCMilliseconds() / 10);
  const secStr = String(seconds).padStart(2, '0');
  const millStr = String(millis).padStart(2, '0');
  if (part === 'seconds') {
    return secStr;
  } else if (part === 'millis') {
    return millStr;
  }
  return `${secStr}:${millStr}`;
};

export const days_ago = (ref: Date) => {
  const refDate = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  let now = new Date();
  now = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // @ts-ignore
  const diff = now - refDate;
  const seconds = diff / 1000;
  return Math.floor(seconds / 86400);
};

export const time_ago = (time_ms: number) => {
  var time_formats: [number, string, number | string][] = [
    [60, 'Sekunden', 1], // 60
    [120, 'Vor 1 Minute', '1 Minute'], // 60*2
    [3600, 'Minuten', 60], // 60*60, 60
    [7200, 'Vor 1 Stunde', '1 hour from now'], // 60*60*2
    [86400, 'Stunden', 3600], // 60*60*24, 60*60
    [172800, 'Gestern', 'Morgen'], // 60*60*24*2
    [604800, 'Tage', 86400], // 60*60*24*7, 60*60*24
    [1209600, 'Letzte Woche', 'Next week'], // 60*60*24*7*4*2
    [2419200, 'Wochen', 604800], // 60*60*24*7*4, 60*60*24*7
    [4838400, 'Vorheriger Monat', 'Next month'], // 60*60*24*7*4*2
    [29030400, 'Monaten', 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
    [58060800, 'Letztes Jahr', 'Next year'], // 60*60*24*7*4*12*2
    [2903040000, 'Jahre', 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
    [5806080000, 'Letztes Jahrzehnt', 'Next century'], // 60*60*24*7*4*12*100*2
    [58060800000, 'Jahrzehnte', 2903040000], // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
  ];
  var seconds = (Date.now() - time_ms) / 1000,
    token = 'Vor',
    list_choice = 1;

  if (seconds === 0) {
    return 'Gerade eben';
  }
  if (seconds < 0) {
    seconds = Math.abs(seconds);
    token = 'ab jetzt';
    list_choice = 2;
  }
  var i = 0,
    format;
  while ((format = time_formats[i++]))
    if (seconds < format[0]) {
      if (typeof format[2] == 'string') return format[list_choice];
      else return `${token} ${Math.floor(seconds / format[2])} ${format[1]}`;
    }
  return time_ms;
};

/**
 * Extract the Mac-Address from `BluetoothEntry` Advertising-Data
 * Device id is encoded as a 6 byte array in the manufacturer data
 * in the form of [0xab, 0xcd, 0xef, 0x01, 0x23, 0x45], which translates
 * to the mac address `AB:CD:EF:01:23:45`
 */
export const extractMac = (device?: BluetoothEntry): string | null => {
  if (!device) return null;
  if (Device.platform === 'android') return device.id;
  const manufacturerData = device.advertising?.['kCBAdvDataManufacturerData'];
  if (!manufacturerData) return null;
  let u8 = new Uint8Array(manufacturerData);
  if (
    typeof manufacturerData === 'object' &&
    manufacturerData?.CDVType === 'ArrayBuffer'
  ) {
    u8 = Uint8Array.from(atob(manufacturerData.data), (c) => c.charCodeAt(0));
  }

  return [...u8]
    .map((v) => _.padStart(v.toString(16), 2, '0'))
    .join(':')
    .toUpperCase();
};

const delim = ['', '_'] as const;
export const deviceIdFromEntry = (device: BluetoothEntry): string => {
  let ret = device.mac?.replaceAll(':', '_');
  if (!ret) {
    const sanitized = device.id.replaceAll('-', '').replaceAll('_', '');
    const joined = _.reduce(sanitized, (acc, v, i) => acc + v + delim[i % 2]);
    ret = joined!.substring(0, 17);
  }
  return ret;
};

export const getVideoFileName = (
  deviceId: string,
  recordId: string
): string => {
  return `Video_${deviceId}_${recordId}.mp4`;
};
export const getVideoFileInfo = (
  deviceId: string,
  recordId: string
): { directory: Directory; path: string } => {
  return {
    directory: Directory.Data,
    path: getVideoFileName(deviceId, recordId),
  };
};
export const getVideoUri = async (
  deviceId: string,
  recordId: string
): Promise<string> => {
  const fileInfo = getVideoFileInfo(deviceId, recordId);

  return (await Filesystem.getUri(fileInfo)).uri;
};
export const getVideoFileUriByNameIfPresent = async (
  filename: string
): Promise<string | null> => {
  const fileInfo = { directory: Directory.Data, path: filename };
  return (await Filesystem.stat(fileInfo).catch(() => ({ uri: null }))).uri;
};
export const getVideoFileUriByName = async (
  filename: string
): Promise<string | null> => {
  const fileInfo = { directory: Directory.Data, path: filename };
  return (await Filesystem.getUri(fileInfo)).uri;
};
export const getVideoFile = async (
  deviceId: string,
  recordId: string
): Promise<ReadFileResult> => {
  const fileInfo = getVideoFileInfo(deviceId, recordId);

  // Read the file from the filesystem.
  // Data is returned as a base64 encoded string, so we need
  // to convert it to a byte array in order to upload it.
  return await Filesystem.readFile(fileInfo);
};

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

////////
/// Logging

const colors = [
  'color:nil',
  'color:#b8ba26;font-weight:bold;',
  'color:#8ebf7b;font-weight:bold;',
  'color:nil',
];

export const log_ = (
  f: (...args: any[]) => void,
  msg: string,
  ...args: any[]
): void => {
  const timeStamp = format(new Date(), 'HH:mm:ss.SSSS');
  f(
    `%c${timeStamp}%c ${msg}`,
    'color:#83a497;font-weight:bold',
    'color:nil;font-weight:nil',
    ...args
  );
};

export const logger = {
  log: _.partial(log_, console.log),
  debug: _.partial(log_, console.debug),
  error: _.partial(log_, console.error),
};

let callId = 1;
function loggedMethod<A, R>(
  func: (...args: A[]) => R,
  clsName: string
): (...args: A[]) => R {
  // const methodName = String(context.name);
  const methodName =
    (func as any).methodName ?? func.name.replace('bound ', '');
  function replacementMethod(...args: A[]): R {
    // const prefix = `[1;32m${cls.name}[0m.[1;36m${func.name.replace('bound ', '')}[0m`;
    const prefix = `${_.padStart(
      callId.toString(),
      4
    )} | %c${clsName}%c.%c${methodName}%c`;

    if (args.length === 0) {
      log_(console.debug, `${prefix} without args`, ...colors);
    } else {
      const lArgs = args.length === 1 ? args[0] : args;
      log_(console.debug, `${prefix} with args: `, ...colors, lArgs);
    }
    const ret = func(...args);
    log_(console.debug, `↳ ${prefix}`, ...colors, ret);
    if (ret instanceof Promise) {
      ret
        .then((result) => {
          log_(
            console.debug,
            `  ↳ ${prefix} – Promise returned`,
            ...colors,
            result
          );
          return result;
        })
        .catch((reason: any) => {
          log_(
            console.debug,
            `  ↳ ${prefix} – Promise threw`,
            ...colors,
            reason
          );
          throw reason;
        });
    }
    callId += 1;
    return ret;
  }
  return replacementMethod;
}
export const loggingClass = <C extends ClassConstructor<any>>(
  cls: C
): ClassConstructor<any> & C => {
  return class extends cls {
    constructor(...args: any[]) {
      super(...args);
      const superProto = Object.getPrototypeOf(Object.getPrototypeOf(this));
      Object.getOwnPropertyNames(superProto).forEach((name) => {
        if (typeof superProto[name] === 'function') {
          Object.defineProperty(this[name], 'name', {
            value: name,
            writable: true,
          });
          this[name] = loggedMethod(superProto[name].bind(this), cls.name);
        }
      });
    }
  };
};

// @ts-ignore
// window.devtoolsFormatters = [
//   {
//     header: (obj: any, config: any) => {
//       if (config && config.noHeader) return null;
//       if (!(obj instanceof ArrayBuffer) || obj.byteLength === 0) return null;

//       const dataView = new DataView(obj);
//       const id = dataView.getUint8(0);
//       const name = packetIdToName(id);
//       return [
//         'div',
//         {},
//         `${name} `,
//         ['object', { object: obj, config: { noHeader: true } }],
//       ];
//     },
//     hasBody: () => {
//       return false;
//     },
//   },
// ];

const BRIDGED_CONSOLE_METHODS = [
  'debug',
  'error',
  'info',
  'log',
  'trace',
  'warn',
];

export const getConsoleMethods = () => {
  return Object.fromEntries(
    BRIDGED_CONSOLE_METHODS.map(
      (name) =>
        // @ts-ignore
        [name, window.console[name].bind(window.console)] as const
    )
  );
};

export const createRecord = async (
  recordId: string,
  samples: ST.RecordSample[],
  bleDevice: BluetoothInfo,
  title: string,
  gpsLocation: ST.Location,
  _user: UserSnapshot,
  stance?: Stance
): Promise<ST.Record> => {
  // Web-Branch: Dummy-Infos verwenden
  // @ts-ignore
  if (typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform?.()) {
    const app_details = {
      version_app: 'web-demo',
      uuid: 'web-demo-uuid',
      name: 'Web Browser',
      model: 'Web',
      platform: 'web',
      operating_system: navigator.platform,
      os_version: navigator.userAgent,
      manufacturer: 'unknown',
      is_virtual: true,
      mem_used: -1,
      disk_free: -1,
      disk_total: -1,
      real_disk_free: -1,
      real_disk_total: -1,
      web_view_version: navigator.userAgent,
      battery_level: -1,
      is_charging: undefined,
      language_code: navigator.language,
      language_tag: navigator.language,
    } as ST.AppDetails;

    const freak_details = {
      version_firmware: 'web-demo-fw',
      version_firmware_os: 'web-demo-fw-os',
    } as ST.FreakDetails;

    const skaterDetails: ST.SkaterDetails = {};

    const record = {
      id_device: bleDevice.entry?.id ?? 'web-demo-device',
      id_record: recordId,
      title: title,
      description: undefined,
      location: gpsLocation,
      tmp_degC: undefined,
      weather: undefined,
      stance: stance,
      discipline: undefined,
      samples,
      participation: undefined,
      freak_details,
      app_details,
      skater_details: skaterDetails,
      board_details: undefined,
    } as ST.Record;
    return record;
  }

  // Native-Branch wie gehabt:
  const deviceId = deviceIdFromEntry(bleDevice.entry!);
  const bleInfo = bleDevice.info!;
  const appInfo = await App.getInfo();
  const app_details = {
    version_app: `${appInfo.version}-${appInfo.build}`,
    uuid: Device.uuid,
    name: Device.name,
    model: Device.model,
    platform: Device.platform,
    operating_system: Device.operatingSystem,
    os_version: Device.osVersion,
    manufacturer: Device.manufacturer,
    is_virtual: Device.isVirtual,
    mem_used: -1,
    disk_free: -1,
    disk_total: -1,
    real_disk_free: -1,
    real_disk_total: -1,
    web_view_version: Device.webViewVersion,
    battery_level: -1,
    is_charging: undefined,
    language_code: undefined,
    language_tag: undefined,
  } as ST.AppDetails;

  const freak_details = {
    version_firmware: bleInfo.firmwareVersion,
    version_firmware_os: bleInfo.firmwareOSVersion,
  } as ST.FreakDetails;

  const skaterDetails: ST.SkaterDetails = {};
  const record = {
    id_device: deviceId,
    id_record: recordId,
    title: title,
    description: undefined,
    location: gpsLocation,
    tmp_degC: undefined,
    weather: undefined,
    stance: stance,
    discipline: undefined,
    samples,
    participation: undefined,
    freak_details,
    app_details,
    skater_details: skaterDetails,
    board_details: undefined,
  } as ST.Record;
  return record;
};

interface TimedElement {
  t_ms?: number;
}
export const calcTime = (
  steps: TimedElement[] | undefined
): number | undefined => {
  if (!steps || !steps.length) return undefined;
  return (steps[steps.length - 1].t_ms! - steps[0].t_ms!) / 1000;
};

export const extractBLEDongleState = (
  state?: DongleState
): BLEDongleState | null => {
  if (!state) return null; // fake it till you make it!

  let versionOS = `${state.version_os_major}.${state.version_os_minor}.${
    state.version_os_patch
  }+${state.version_os_build ?? '0'}`;
  let versionFW = `${state.version_fw_major}.${state.version_fw_minor}.${
    state.version_fw_patch
  }+${state.version_fw_build ?? '0'}`;

  return {
    versionOS,
    versionFW,
    statusUSB: _BLEStatus[state.status_usb] as BLEStatus,
    statusBLE: _BLEStatus[state.status_ble] as BLEStatus,
    statusBNO08X: _BLEStatus[state.status_bno08x] as BLEStatus,
    statusDPS310: _BLEStatus[state.status_dps310] as BLEStatus,
    statusLED: _BLEStatus[state.status_led] as BLEStatus,
    statusButton: _BLEStatus[state.status_button] as BLEStatus,
    statusBattery: _BLEStatus[state.status_battery] as BLEStatus,
    batteryLevel: state.battery_level,
  };
};

export const toSpeed = (vec: ST.Vector3D | undefined): number => {
  if (!vec) return 0;
  return new Vector3(vec.x, vec.y, vec.z).length() * 3.6;
};

export const extractRenderData = (play: ST.Play): RenderData => {
  const ret: RenderData = {
    names: [],
    speed: [],
  };

  const steps = play.steps!;
  steps.forEach((step, stepIndex) => {
    if (stepIndex === steps.length - 1) return;
    ret.speed.push({
      start: step.t_ms!, // ✅ Keep non-null assertion
      end: steps[stepIndex + 1]?.t_ms ?? -1, // ✅ Already safe with fallback
      value: toSpeed(step.v_ms),
      suffix: ' km/h',
    });
  });
  play.figures?.forEach((fig) => {
    const start = steps[fig.i_beg!].t_ms!; // ✅ Keep non-null assertion
    const end = steps[fig.i_end!].t_ms!; // ✅ Keep non-null assertion
    ret.names.push({
      start,
      end,
      value: fig.name!,
      suffix: '',
    });
  });

  // collect highest values for some details
  const detailKeys = _.keysIn(new ST.PlayDetails()) as (keyof ST.PlayDetails)[];

  detailKeys.forEach((detail) => {
    const detailCollection: TimedData<number>[] = [];
    let highestValue = -1;
    let acc = 0;
    let interpolate =
      detail.startsWith('fly') || detail === 'travel_distance_m';
    let lastValue: number | undefined = interpolate ? 0 : undefined;
    let suffix = '';
    if (detail.endsWith('_m')) suffix = ' m';
    else if (detail.endsWith('_s')) suffix = ' s';

    play.sections?.forEach((sec) => {
      const start = steps[sec.i_beg!].t_ms!; // ✅ Keep non-null assertion
      const end = steps[sec.i_end!].t_ms!; // ✅ Keep non-null assertion
      const theValue = sec.details![detail];
      if (typeof theValue !== 'number') return;

      let value = theValue;
      // only 'fly_height' & 'fly_width' should use max values
      if (detail.startsWith('fly')) {
        value = theValue;
        // value = Math.max(highestValue, theValue);
        highestValue = value;
      } else if (detail === 'travel_distance_m') {
        acc += theValue;
        value = acc;
      }

      // ret[detail]!
      detailCollection.push({
        start, // ✅ Now guaranteed to be number
        end,   // ✅ Now guaranteed to be number
        value,
        lastValue,
        suffix,
      });

      if (interpolate) lastValue = value;
    });

    if (detailCollection.length !== 0) {
      (ret as any)[detail] = detailCollection;
    }
  });

  return ret as RenderData;
};

export const withTimeout = <T>(
  ms: number,
  timeoutMsg: string,
  func: () => Promise<T>
): Promise<T> => {
  return new Promise(async (resolve, reject) => {
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      reject(new Error(timeoutMsg));
    }, ms);
    const ret = await func();
    clearTimeout(timeout);
    if (!timedOut) resolve(ret);
  });
};

export const setupLogging = (
  originalMethods: ReturnType<typeof getConsoleMethods>
) => {
  const serializeConsoleMessage = (msg: any) => {
    if (typeof msg === 'object') {
      try {
        msg = JSON.stringify(msg);
      } catch (e: any) {
        // ignore
      }
    }
    return String(msg);
  };

  // @ts-ignore
  const cap = window.Capacitor;
  if (cap && cap.toNative && cap.isPluginAvailable('Console')) {
    Object.defineProperties(
      window.console,
      BRIDGED_CONSOLE_METHODS.reduce((props, method) => {
        const consoleMethod = originalMethods[method]!;
        // @ts-ignore
        if (!consoleMethod['overridden']) {
          // @ts-ignore
          props[method] = {
            value: (...args: any[]) => {
              const msgs = [...args];
              cap.toNative('Console', 'log', {
                level: method,
                message: msgs.map(serializeConsoleMessage).join(' '),
                args: msgs.map(serializeConsoleMessage),
              });
              return consoleMethod(...args);
            },
          };
          // @ts-ignore
          props[method]['overridden'] = true;
        }
        return props;
      }, {})
    );
  }
};

/**
 * Korrigiert Timing-Ausreißer in Record-Samples (t_ms).
 * Anpassung erfolgt, wenn der Abstand zwischen zwei Samples >50ms oder <5ms ist.
 */
export function recordPreProcessing(
  recordData: { t_ms?: number }[], // <-- t_ms jetzt optional!
  logger: any,
  LOG_PREFIX = ''
) {
  for (let i = 0; i < recordData.length - 1; i++) {
    let a = recordData.at(i)?.t_ms;
    let n = recordData.at(i + 1)?.t_ms;
    if (a === undefined) {
      logger.error(
        `${LOG_PREFIX} fixRecordSampleTimings - Record sample at index ${i} is undefined`
      );
      break;
    }
    if (n === undefined) {
      logger.error(
        `${LOG_PREFIX} fixRecordSampleTimings - Record sample at index ${i + 1} is undefined`
      );
      break;
    }
    let diff = n - a;

    if (Math.abs(diff) > 50 || diff < 5) {
      logger.warn(`${LOG_PREFIX} fixRecordSampleTimings - Fixing timing values`, {
        index: i,
        currentTime: a,
        nextTime: n,
        difference: diff,
        fixingRequired: true,
      });

      // Add the difference to all subsequent samples
      for (let j = i + 1; j < recordData.length; j++) {
        if (j >= recordData.length) {
          logger.warn(
            `${LOG_PREFIX} fixRecordSampleTimings - End of record samples reached at index ${j}`
          );
          break;
        }
        const sample = recordData.at(j);
        if (sample && sample.t_ms !== undefined) {
          const originalTime = sample.t_ms;
          sample.t_ms -= diff - 10;
          recordData[j] = sample;

          if (j < i + 5) {
            // Log first few corrections only
            logger.debug(`${LOG_PREFIX} fixRecordSampleTimings - Time correction`, {
              sampleIndex: j,
              originalTime,
              correctedTime: sample.t_ms,
              correction: diff - 10,
            });
          }
        }
      }
    }
  }
}
